/**
 * OnboardingGuard - Onboarding gate for optra-ai dashboard.
 *
 * This component wraps the main dashboard content and:
 * 1. Checks if the user needs onboarding (via optra-ai's API)
 * 2. Redirects to /onboarding page if provisioning is needed
 * 3. Handles connection errors (backend unreachable)
 */

import { useState, useEffect, useCallback, useRef, type ReactNode } from 'react'
import { CloudOff } from 'lucide-react'
import { backendApi, BackendApiError } from '@/lib/api/backend'
import {
  getOnboardingStatus,
  isOnboardingErrorResponse,
  type OnboardingStep,
} from '@/lib/onboarding'
import { ProvisioningProgress } from './ProvisioningProgress'
import { Progress } from '@/components/ui/progress'

interface OnboardingGuardProps {
  children: ReactNode
}

type OnboardingState = 'checking' | 'ready' | 'error' | 'service-unavailable'

/**
 * Detects if an error is a connection/network error (backend unreachable).
 */
function isConnectionError(error: unknown): boolean {
  if (!error || typeof error !== 'object') return false
  const axiosError = error as { code?: string; message?: string; response?: unknown }
  // No response means the request never reached the server
  if (axiosError.response) return false
  return (
    axiosError.code === 'ERR_NETWORK' ||
    axiosError.code === 'ECONNREFUSED' ||
    (axiosError.message?.toLowerCase().includes('network error') ?? false)
  )
}

/**
 * OnboardingGuard component that handles zero-configuration onboarding.
 *
 * Usage:
 * ```tsx
 * <OnboardingGuard>
 *   {children}
 * </OnboardingGuard>
 * ```
 */
export function OnboardingGuard({ children }: OnboardingGuardProps) {
  const [state, setState] = useState<OnboardingState>('checking')
  const [error, setError] = useState<string | null>(null)
  const [failedStep, setFailedStep] = useState<OnboardingStep | null>(null)
  const [isRetryable, setIsRetryable] = useState<boolean>(true)

  // Ref to prevent concurrent provisioning calls (React StrictMode protection)
  const provisioningInProgress = useRef(false)

  const checkAndProvision = useCallback(async () => {
    // Prevent concurrent calls (React StrictMode causes double renders)
    if (provisioningInProgress.current) {
      console.log('[OnboardingGuard] Provisioning already in progress, skipping')
      return
    }

    // Mark as in progress to prevent concurrent calls
    provisioningInProgress.current = true

    try {
      setState('checking')
      setError(null)

      // First, check if user is authenticated
      let session
      try {
        session = await backendApi.auth.getSession()
      } catch (err) {
        if (err instanceof BackendApiError && (err.status === 401 || err.status === 403)) {
          console.log('[OnboardingGuard] User not authenticated, redirecting to signin')
          window.location.href = '/signin'
          return
        }
        throw err
      }

      if (!session?.user) {
        console.log('[OnboardingGuard] No user in session, redirecting to signin')
        window.location.href = '/signin'
        return
      }

      // Check onboarding status
      console.log('[OnboardingGuard] Checking onboarding status...')
      const status = await getOnboardingStatus()
      console.log('[OnboardingGuard] Onboarding status:', status)

      if (status.needsOnboarding) {
        // Redirect to dedicated onboarding page which shows progress and error details
        console.log('[OnboardingGuard] User needs onboarding, redirecting to /onboarding...')
        window.location.href = '/onboarding'
        return
      }

      setState('ready')
    } catch (err: unknown) {
      console.error('[OnboardingGuard] Error:', err)

      // Check if this is a connection error (backend unreachable)
      if (isConnectionError(err)) {
        console.log('[OnboardingGuard] Connection error detected, showing service unavailable screen')
        setState('service-unavailable')
        return
      }

      // Extract error message and step info with detailed logging
      let message = 'An unexpected error occurred during setup'
      let step: OnboardingStep | null = null
      let retryable = true

      if (typeof err === 'object' && err !== null) {
        // Handle Axios error response
        const axiosError = err as { response?: { status?: number; data?: unknown }; message?: string }
        console.error('[OnboardingGuard] Error response status:', axiosError.response?.status)
        console.error('[OnboardingGuard] Error response data:', JSON.stringify(axiosError.response?.data, null, 2))

        const errorData = axiosError.response?.data

        // Check if this is an OnboardingErrorResponse with step info
        if (isOnboardingErrorResponse(errorData)) {
          message = errorData.error.message
          step = errorData.error.step
          retryable = errorData.error.isRetryable
          console.log('[OnboardingGuard] Onboarding error at step:', step, 'retryable:', retryable)
        } else {
          // Fall back to generic error extraction
          const genericError = errorData as { error?: { message?: string } } | undefined
          if (genericError?.error?.message) {
            message = genericError.error.message
          } else if (err instanceof BackendApiError) {
            message = err.message
          } else if (axiosError.message) {
            message = axiosError.message
          }
        }
      } else if (err instanceof Error) {
        message = err.message
      }

      setError(message)
      setFailedStep(step)
      setIsRetryable(retryable)
      setState('error')
    } finally {
      // Reset flag to allow retry
      provisioningInProgress.current = false
    }
  }, [])

  // Handle sign out from modal
  const handleSignOut = useCallback(async () => {
    try {
      await backendApi.auth.signout()
    } catch (err) {
      console.error('[OnboardingGuard] Sign out error:', err)
    }
    window.location.href = '/signin'
  }, [])

  useEffect(() => {
    checkAndProvision()
  }, [checkAndProvision])

  const handleRetry = useCallback(() => {
    // Reset error state before retrying
    setError(null)
    setFailedStep(null)
    setIsRetryable(true)
    checkAndProvision()
  }, [checkAndProvision])

  if (state === 'checking') {
    return <LoadingScreen message="Checking your account..." />
  }

  if (state === 'service-unavailable') {
    return <ServiceUnavailableScreen onRetry={handleRetry} />
  }

  if (state === 'error') {
    // If we have step info, show progress with failure state
    if (failedStep) {
      return (
        <ProvisioningProgressWithActions
          failedStep={failedStep}
          errorMessage={error}
          isRetryable={isRetryable}
          onRetry={handleRetry}
          onSignOut={handleSignOut}
        />
      )
    }
    // Otherwise show generic error screen
    return <ErrorScreen message={error} onRetry={handleRetry} onSignOut={handleSignOut} />
  }

  return <>{children}</>
}

/**
 * Loading screen component displayed during onboarding checks and provisioning.
 */
function LoadingScreen({ message }: { message: string }) {
  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50 dark:bg-[#00000f]">
      <div className="flex flex-col items-center gap-4">
        {/* Spinner */}
        <div className="h-12 w-12 animate-spin rounded-full border-4 border-gray-200 dark:border-zinc-700 border-t-blue-600 dark:border-t-blue-400" />
        <p className="text-gray-500 dark:text-zinc-400 text-sm">{message}</p>
      </div>
    </div>
  )
}

/**
 * Service unavailable screen displayed when backend is unreachable.
 * Features auto-retry with countdown and manual retry button.
 */
function ServiceUnavailableScreen({ onRetry }: { onRetry: () => void }) {
  const RETRY_INTERVAL = 5 // seconds
  const [countdown, setCountdown] = useState(RETRY_INTERVAL)
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null)

  useEffect(() => {
    // Start countdown timer
    timerRef.current = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          // Time to retry
          return RETRY_INTERVAL
        }
        return prev - 1
      })
    }, 1000)

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current)
      }
    }
  }, [])

  // Separate effect to handle the actual retry
  const hasInitialized = useRef(false)
  useEffect(() => {
    if (!hasInitialized.current) {
      hasInitialized.current = true
      return
    }
    if (countdown === RETRY_INTERVAL) {
      onRetry()
    }
  }, [countdown, onRetry])

  const handleManualRetry = () => {
    setCountdown(RETRY_INTERVAL)
    onRetry()
  }

  const progressValue = ((RETRY_INTERVAL - countdown) / RETRY_INTERVAL) * 100

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50 dark:bg-[#00000f] p-4">
      <div className="max-w-md w-full">
        <div className="bg-gray-100 dark:bg-zinc-900/50 border border-gray-200 dark:border-zinc-800 rounded-lg p-8 text-center">
          <div className="mb-6">
            <CloudOff className="mx-auto h-12 w-12 text-gray-400 dark:text-zinc-500" strokeWidth={1.5} />
          </div>

          <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
            Service Temporarily Unavailable
          </h2>

          <p className="text-gray-500 dark:text-zinc-400 text-sm mb-6">
            We're having trouble connecting to our servers. This is usually temporary.
          </p>

          <div className="mb-6">
            <Progress value={progressValue} className="h-1.5 mb-2" />
            <p className="text-gray-400 dark:text-zinc-500 text-xs">
              Retrying in {countdown}s...
            </p>
          </div>

          <button
            onClick={handleManualRetry}
            className="inline-flex items-center justify-center rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    </div>
  )
}

/**
 * Get user-friendly error info based on the error message.
 */
function getErrorInfo(message: string | null): {
  title: string
  description: string
  showRetry: boolean
  showSignOut: boolean
} {
  const lowerMsg = message?.toLowerCase() || ''

  if (lowerMsg.includes('company email')) {
    return {
      title: 'Company Email Required',
      description: message || 'Please sign up with your company email address to use OptraAI.',
      showRetry: false,
      showSignOut: true,
    }
  }

  if (lowerMsg.includes('already exists') || lowerMsg.includes('already registered')) {
    return {
      title: 'Organization Already Exists',
      description: 'An organization with this name already exists. Please contact your administrator to be added to the existing organization, or try again with a different company email.',
      showRetry: true,
      showSignOut: true,
    }
  }

  if (lowerMsg.includes('unauthorized') || lowerMsg.includes('authentication')) {
    return {
      title: 'Session Expired',
      description: 'Your session has expired. Please sign in again.',
      showRetry: false,
      showSignOut: true,
    }
  }

  if (lowerMsg.includes('forbidden') || lowerMsg.includes('permission')) {
    return {
      title: 'Access Denied',
      description: message || 'You do not have permission to perform this action.',
      showRetry: false,
      showSignOut: true,
    }
  }

  return {
    title: 'Setup Failed',
    description: message || 'An error occurred during setup. Please try again.',
    showRetry: true,
    showSignOut: true,
  }
}

/**
 * Error screen component displayed when onboarding fails.
 */
function ErrorScreen({
  message,
  onRetry,
  onSignOut,
}: {
  message: string | null
  onRetry: () => void
  onSignOut: () => void
}) {
  const errorInfo = getErrorInfo(message)

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50 dark:bg-[#00000f] p-4">
      <div className="max-w-md w-full">
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800/30 rounded-lg p-6 text-center">
          <div className="mb-4">
            <svg
              className="mx-auto h-12 w-12 text-red-500 dark:text-red-400"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
              />
            </svg>
          </div>

          <h2 className="text-lg font-semibold text-red-600 dark:text-red-400 mb-2">
            {errorInfo.title}
          </h2>

          <p className="text-red-500 dark:text-red-400/80 text-sm mb-6">
            {errorInfo.description}
          </p>

          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            {errorInfo.showRetry && (
              <button
                onClick={onRetry}
                className="inline-flex items-center justify-center rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 transition-colors"
              >
                Try Again
              </button>
            )}
            {errorInfo.showSignOut && (
              <button
                onClick={onSignOut}
                className="inline-flex items-center justify-center rounded-md bg-gray-200 dark:bg-zinc-800 px-4 py-2 text-sm font-medium text-gray-700 dark:text-zinc-300 hover:bg-gray-300 dark:hover:bg-zinc-700 transition-colors"
              >
                Sign Out
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

/**
 * Provisioning progress display with action buttons for error state.
 * Shows step-by-step progress with the failed step highlighted.
 */
function ProvisioningProgressWithActions({
  failedStep,
  errorMessage,
  isRetryable,
  onRetry,
  onSignOut,
}: {
  failedStep: OnboardingStep
  errorMessage: string | null
  isRetryable: boolean
  onRetry: () => void
  onSignOut: () => void
}) {
  return (
    <ProvisioningProgress
      isProvisioning={false}
      failedStep={failedStep}
      errorMessage={errorMessage}
      actions={
        <>
          {isRetryable && (
            <button
              onClick={onRetry}
              className="inline-flex items-center justify-center rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 transition-colors"
            >
              Try Again
            </button>
          )}
          <button
            onClick={onSignOut}
            className="inline-flex items-center justify-center rounded-md bg-gray-200 dark:bg-zinc-800 px-4 py-2 text-sm font-medium text-gray-700 dark:text-zinc-300 hover:bg-gray-300 dark:hover:bg-zinc-700 transition-colors"
          >
            Sign Out
          </button>
        </>
      }
    />
  )
}

